// ChatLauncher.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiExternalLink,
  FiPaperclip,
  FiMic,
  FiArrowRight,
  FiMinus,
} from "react-icons/fi";
import chatLogo from "./assets/chatLogo.png";
import chatFAB from "./assets/chatFAB.png";
import PropTypes from "prop-types";
import PersonIcon from "@mui/icons-material/Person";
import CheckIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";

// backdrop for both mini & full
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

// Launcher FAB
const LauncherButton = styled(motion.button)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 120px;
  height: 120px;
  border: none;
  background: none;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const IconImg = styled.img`
  width: 120px;
  height: 120px;
`;

// Mini chat window
const SmallWindow = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 420px;
  height: calc(100vh - 48px);
  background: linear-gradient(180deg, #ffffff 0%, #f0ebfe 54.39%, #e3dbfe 100%);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
`;

// Full-screen overlay
const FullOverlay = styled(motion.div)`
  position: fixed;
  top: 18px;
  left: 18px;
  width: calc(100vw - 36px);
  height: calc(100vh - 36px);
  border-radius: 15px;
  background: linear-gradient(135deg, #f7f8fc 0%, #ede9ff 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
`;

// Shared header controls
const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 16px;
  position: relative;
`;
const Logo = styled.img`
  width: 32px;
  height: 32px;
`;
const ExpandBtn = styled.button`
  position: absolute;
  right: 48px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;
const MinimizeBtn = styled.button`
  position: absolute;
  right: 48px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;
const CloseBtn = styled.button`
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;

// Mini-window options
const Options = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const OptionCard = styled.button`
  background: #fff;
  border: 2px solid ${({ active }) => (active ? "#144A8C" : "#e0e0e0")};
  border-radius: 12px;
  padding: 12px;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  &:hover {
    border-color: #144a8c;
  }
`;
const OptionTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  margin-bottom: 4px;
`;
const OptionDesc = styled.div`
  font-size: 12px;
  color: #666;
`;

// Shared input bar (static in mini)
const InputBar = styled.div`
  display: flex;
  align-items: center;
  margin: 16px auto 40px;
  max-width: 90%;
  padding: 12px 16px;
  border-radius: 39px;
  border-width: 1px;
  border: 1px solid #8c8c8c1a;
  backdrop-filter: blur(0px);
  background: #8c8c8c3d;
  align-self: center;
`;
const AttachBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  margin-right: 8px;
  cursor: pointer;
`;
const TextInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  padding: 8px;
  background: transparent;
  width: 50%;
`;
const MicBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  margin-right: 8px;
  cursor: pointer;
`;
const SendBtn = styled.button`
  background: #144a8c;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MessagesList = styled.div`
  flex: 1;
  padding: 0 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;
  justify-content: ${({ $isUser }) => ($isUser ? "flex-end" : "flex-start")};
`;

const Chip = styled.div`
  min-width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  margin-top: 10px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ role }) => (role === "assistant" ? "#144A8C" : "#8c8c8c")};
`;

const Bubble = styled.div`
  max-width: 70%;
  padding: 12px;
  border-radius: 16px;
  background: ${({ role }) => (role === "user" ? "#144a8c" : "#fff")};
  color: ${({ role }) => (role === "user" ? "#fff" : "#000")};
  align-self: ${({ role }) => (role === "user" ? "flex-end" : "flex-start")};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

// Lawyer card components
const LawyerCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 16px;
  margin-bottom: 12px;
`;

const LawyerCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
`;

const LawyerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  width: 100%;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-bottom: 8px;
`;

const LawyerName = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const LawyerDetails = styled.div`
  font-size: 12px;
  color: #666;
  margin: 4px 0;
`;

const BookButton = styled.button`
  background: #144a8c;
  color: #fff;
  border: none;
  padding: 8px;
  border-radius: 8px;
  font-size: 12px;
  margin-top: 8px;
  cursor: pointer;
`;

const Disclaimer = styled.div`
  font-size: 10px;
  color: #999;
  font-family: Poppins;
  padding: 0 16px;
  margin-top: 8px;
  line-height: 1.2;
`;

const RefineButton = styled.button`
  background: #144a8c;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  margin: 0 16px 16px;
  cursor: pointer;
  font-weight: 500;
  align-self: flex-end;
`;

const PillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin: 8px 16px 16px;
`;

const Pill = styled.button`
  padding: 14px 18px;
  border-radius: 9999px;
  font-weight: 500;
  font-size: 15px;
  border: 2px solid #144a8c;
  background: ${({ $active }) => ($active ? "#e6ecff" : "transparent")};
  color: #144a8c;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  svg {
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.2s;
  }
`;

const ApplyBtn = styled.button`
  align-self: flex-end;
  background: #144a8c;
  color: #fff;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
`;

const DebugInfo = styled.div`
  font-size: 10px;
  color: #999;
  background-color: #f0f0f0;
  padding: 4px;
  margin: 4px 16px;
  border-radius: 4px;
  display: ${process.env.NODE_ENV === "development" ? "block" : "none"};
`;

// PillFilterPanel - Matching ChatWindow implementation
function PillFilterPanel({ onSubmit, onCancel }) {
  const [state, setState] = React.useState({
    experience: false,
    affordability: false,
    gender: false,
    outcomes: false,
    communicator: false,
    other: false,
  });

  const toggle = (key) => setState((s) => ({ ...s, [key]: !s[key] }));

  const buildFilters = () => {
    const filters = {};
    if (state.experience) filters.experience = ">=10 years";
    if (state.gender) filters.gender = "female";
    if (state.affordability) filters.affordability = "very_affordable";
    if (state.other) filters.other = "custom";
    return filters;
  };

  return (
    <>
      <div style={{ fontSize: 12, color: "#666", margin: "4px 0 4px 16px" }}>
        (Please select all that apply)
      </div>

      <PillGrid>
        <Pill $active={state.experience} onClick={() => toggle("experience")}>
          At least 10+ years of experience
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill
          $active={state.affordability}
          onClick={() => toggle("affordability")}
        >
          Must be very affordable
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill $active={state.gender} onClick={() => toggle("gender")}>
          I prefer a female lawyer
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill $active={state.outcomes} onClick={() => toggle("outcomes")}>
          Focus on winning rate / outcomes
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill
          $active={state.communicator}
          onClick={() => toggle("communicator")}
        >
          Clear communicator, explains things well
          <CheckIcon fontSize="small" />
        </Pill>

        <Pill $active={state.other} onClick={() => toggle("other")}>
          Other (please describe)
          <CheckIcon fontSize="small" />
        </Pill>
      </PillGrid>

      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          padding: "0 16px 16px",
        }}
      >
        <button
          onClick={onCancel}
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          Cancel
        </button>
        <ApplyBtn onClick={() => onSubmit(buildFilters())}>
          See Better Matches
        </ApplyBtn>
      </div>
    </>
  );
}

// LawyerCardBlock component - Matching ChatWindow implementation
function LawyerCardBlock({ lawyers, navigate }) {
  if (!lawyers || lawyers.length === 0) return null;

  return (
    <>
      <LawyerCardsContainer>
        <LawyerGrid>
          {lawyers.map((L) => (
            <LawyerCard key={L.user_id}>
              <Avatar
                src={L.profile_photo_url || "https://via.placeholder.com/48"}
              />
              <LawyerName>{L.name}</LawyerName>

              <LawyerDetails>
                {L.years_of_experience} yrs •{" "}
                {Array.isArray(L.practice_areas)
                  ? L.practice_areas[0]
                  : L.practice_area}
              </LawyerDetails>

              <LawyerDetails>
                ⭐ {Number(L.average_rating || 0).toFixed(2)} •{" "}
                {L.number_of_reviews || 0} reviews
              </LawyerDetails>

              <LawyerDetails>
                📍 {L.city}, {L.state}
              </LawyerDetails>

              <LawyerDetails>Next Available Today</LawyerDetails>

              <BookButton
                onClick={() => navigate(`/lawyer-detail/${L.user_id}`)}
              >
                Book online
              </BookButton>
            </LawyerCard>
          ))}
        </LawyerGrid>
      </LawyerCardsContainer>
      <Disclaimer>
        Please Note: All information you provide is confidential. These
        conversations are not legal advice.
      </Disclaimer>
    </>
  );
}

export default function ChatLauncher({
  mode, // "fab" or "small"
  options, // array of cards
  selectedCard,
  hasSentFirst,
  messages,
  lawyerCards,
  inputValue,
  lastIds,
  showFilters,
  setShowFilters,
  onFabClick,
  onCardSelect,
  onExpand,
  onInputChange,
  onSend,
  onClose,
  onMinimize,
}) {
  const endRef = useRef(null);
  const navigate = useNavigate();

  // scroll on new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Debug log when lawyerCards changes
  useEffect(() => {
    console.log("ChatLauncher received lawyerCards:", lawyerCards);
  }, [lawyerCards]);

  if (mode === "fab") {
    return (
      <LauncherButton
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        onClick={onFabClick}
        aria-label="Open chat"
      >
        <IconImg src={chatFAB} alt="Chat" />
      </LauncherButton>
    );
  }

  // Refine matches handler
  const handleRefineMatches = () => {
    // Get lawyer IDs from the current cards
    if (lawyerCards && lawyerCards.length > 0) {
      const lawyer_ids = lastIds;
      // Send a message to refine (will trigger the refine_lawyers function)
      onInputChange(
        JSON.stringify({
          refinement_request: true,
          lawyer_ids: lawyer_ids,
          filters: {},
        })
      );
      onSend();
    }
  };

  if (mode === "small") {
    return (
      <>
        <AnimatePresence>
          <Backdrop onClick={onClose} />
        </AnimatePresence>

        <SmallWindow
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.2 }}
        >
          <Header>
            <Logo src={chatLogo} alt="Logo" />

            <MinimizeBtn onClick={onMinimize} aria-label="Minimize">
              <FiMinus />
            </MinimizeBtn>

            <ExpandBtn onClick={onExpand} aria-label="Expand">
              <FiExternalLink />
            </ExpandBtn>

            <CloseBtn onClick={onClose} aria-label="Close">
              <FiX />
            </CloseBtn>
          </Header>

          {!hasSentFirst ? (
            <Options>
              {options.map((o) => (
                <OptionCard
                  key={o.key}
                  active={o.key === selectedCard}
                  onClick={() => onCardSelect(o.key)}
                >
                  <OptionTitle>
                    {o.icon}
                    <span style={{ marginLeft: 8 }}>{o.title}</span>
                  </OptionTitle>
                  <OptionDesc>{o.desc}</OptionDesc>
                </OptionCard>
              ))}
            </Options>
          ) : (
            <>
              {/* <MessagesList>
                {messages.map((m, i) => {
                  if (m.role === "cards") {
                    return (
                      <React.Fragment key={i}>
                        <LawyerCardBlock
                          lawyers={m.payload}
                          navigate={navigate}
                        />

                        {showFilters ? (
                          <PillFilterPanel
                            onCancel={() => setShowFilters(false)}
                            onSubmit={(filters) => {
                              setShowFilters(false); // close panel
                              onInputChange(JSON.stringify(filters)); // JSON‑only msg
                              onSend(); // triggers refine flow
                            }}
                          />
                        ) : (
                          <RefineButton onClick={() => setShowFilters(true)}>
                            See Better Matches
                          </RefineButton>
                        )}
                      </React.Fragment>
                    );
                  }

                  return (
                    <React.Fragment key={i}>
                      <Row $isUser={m.role === "user"}>
                        {m.role === "assistant" && (
                          <Chip role="assistant">QB</Chip>
                        )}

                        <Bubble role={m.role}>{m.content}</Bubble>

                        {m.role === "user" && (
                          <Chip role="user">
                            <PersonIcon style={{ fontSize: 16 }} />
                          </Chip>
                        )}
                      </Row>

                      {m.role === "assistant" && (
                        <Disclaimer>
                          Please Note: All information you provide is
                          confidential. These conversations are not legal
                          advice.
                        </Disclaimer>
                      )}
                    </React.Fragment>
                  );
                })}
                <div ref={endRef} />
              </MessagesList> */}

              {/* ─────────── MESSAGE STREAM + CARD/FILTER UI ─────────── */}
              <MessagesList>
                {messages.map((m, i) => {
                  /* ① ── lawyer grid that arrived through the proper {role:"cards"} msg */
                  if (m.role === "cards") {
                    return (
                      <React.Fragment key={i}>
                        <LawyerCardBlock
                          lawyers={m.payload}
                          navigate={navigate}
                        />

                        {showFilters ? (
                          <PillFilterPanel
                            onCancel={() => setShowFilters(false)}
                            onSubmit={(filters) => {
                              setShowFilters(false); // close panel
                              onInputChange(JSON.stringify(filters)); // JSON‑only msg
                              onSend(); // triggers refine
                            }}
                          />
                        ) : (
                          <RefineButton onClick={() => setShowFilters(true)}>
                            See Better Matches
                          </RefineButton>
                        )}
                      </React.Fragment>
                    );
                  }

                  /* ② ── normal chat bubbles */
                  return (
                    <React.Fragment key={i}>
                      <Row $isUser={m.role === "user"}>
                        {m.role === "assistant" && (
                          <Chip role="assistant">QB</Chip>
                        )}

                        <Bubble role={m.role}>{m.content}</Bubble>

                        {m.role === "user" && (
                          <Chip role="user">
                            <PersonIcon style={{ fontSize: 16 }} />
                          </Chip>
                        )}
                      </Row>

                      {m.role === "assistant" && (
                        <Disclaimer>
                          Please Note: All information you provide is
                          confidential. These conversations are not legal
                          advice.
                        </Disclaimer>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* automatic scroll anchor */}
                <div ref={endRef} />
              </MessagesList>

              {/* ─────────── FALLBACK: model forgot role:"cards" but state has rows ─────────── */}
              {!messages.some((m) => m.role === "cards") &&
                lawyerCards?.length > 0 && (
                  <>
                    <LawyerCardBlock
                      lawyers={lawyerCards}
                      navigate={navigate}
                    />

                    {showFilters ? (
                      <PillFilterPanel
                        onCancel={() => setShowFilters(false)}
                        onSubmit={(filters) => {
                          setShowFilters(false);
                          onInputChange(JSON.stringify(filters));
                          onSend();
                        }}
                      />
                    ) : (
                      <RefineButton onClick={() => setShowFilters(true)}>
                        See Better Matches
                      </RefineButton>
                    )}
                  </>
                )}

              {process.env.NODE_ENV === "development" && (
                <DebugInfo>
                  lawyerCards state:{" "}
                  {lawyerCards
                    ? `${lawyerCards.length} lawyers available`
                    : "null"}
                </DebugInfo>
              )}
            </>
          )}

          <InputBar>
            <AttachBtn aria-label="Attach">
              <FiPaperclip />
            </AttachBtn>
            <TextInput
              placeholder={
                hasSentFirst ? "Type your message…" : "Type your first message…"
              }
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSend()}
            />
            <MicBtn aria-label="Record">
              <FiMic />
            </MicBtn>
            <SendBtn onClick={onSend} aria-label="Send">
              <FiArrowRight />
            </SendBtn>
          </InputBar>
        </SmallWindow>
      </>
    );
  }

  return null;
}

ChatLauncher.propTypes = {
  mode: PropTypes.oneOf(["fab", "small"]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.node.isRequired,
      title: PropTypes.string.isRequired,
      desc: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedCard: PropTypes.string.isRequired,
  hasSentFirst: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(["system", "user", "assistant", "cards"])
        .isRequired,
      content: PropTypes.string,
      payload: PropTypes.array,
    })
  ).isRequired,
  lawyerCards: PropTypes.array,
  inputValue: PropTypes.string.isRequired,
  lastIds: PropTypes.array,
  showFilters: PropTypes.bool,
  setShowFilters: PropTypes.func,
  onFabClick: PropTypes.func.isRequired,
  onCardSelect: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func,
};

ChatLauncher.defaultProps = {
  lawyerCards: [],
  lastIds: [],
  showFilters: false,
  setShowFilters: () => {},
  onMinimize: () => {},
};
